import { FunctionsHttpError } from "@supabase/functions-js";
import { FunctionInvokeOptions } from "@supabase/supabase-js";

import googleAuthorizationTokenService from "@/utils/google-authorization-service";
import { supabase } from "..";

export async function executeSupabaseEdgeFunction<T = unknown>(
  url: string,
  options?: Omit<FunctionInvokeOptions, "region">,
): Promise<T> {
  // Execute the Supabase function
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { error, data } = await supabase.functions.invoke<T>(url, { method: "GET", ...options });

  // If function executed without error, return the data
  if (!error) return data!;

  // We only handle http response code errors here; any other errors can be thrown directly
  if (!(error instanceof FunctionsHttpError)) throw error;

  // Try to extract and log the request id from the 'x-request-id' header
  const response = error.context as Response;
  const requestId = response.headers.get("x-request-id");
  if (requestId) console.warn(`An error occurred in Supabase edge function with request id ${requestId}`);

  // Only handle JSON error responses
  const contentType = response.headers.get("content-type");
  if (!contentType?.includes("application/json")) throw error;
  const errorData = (await response.json()) as unknown;

  // For Google auth errors, trigger the UX flow to fetch a new auth token, then retry the request
  if (
    response.status === 401 &&
    errorData &&
    typeof errorData === "object" &&
    "error" in errorData &&
    errorData.error === "google_auth_required"
  ) {
    console.info("Google authorization required. Starting new token request");
    await googleAuthorizationTokenService.authorizeCurrentUser();
    console.info("Google authorization completed successfully. Retrying Supabase edge function request");
    return await executeSupabaseEdgeFunction<T>(url, options);
  }

  // Otherwise, we don't know how to handle this error, so re-throw it
  throw error;
}
